/**
 * Sidebar Component - Layout
 * Navigation sidebar with collapsible sections and active route highlighting
 * Uses Tailwind for layout styling
 */

import { useState, useCallback, useMemo, type ComponentType } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Circle, ArrowLeftRight } from 'lucide-react';
import * as icons from 'lucide-react';
import fiadoIsotipo from '@/assets/fiado-isotipo.png';
import type { LucideProps } from 'lucide-react';
import type { SidebarSection } from '@/constants';
import { useAppStore } from '@/stores/app.store';
import { useAuthStore } from '@/stores/auth.store';
import { useAudience } from '@/hooks/useAudience';
import { cn } from '@/lib/utils';
import { filterSectionsByPermissions } from '@/lib/sidebar-permissions';

type IconComponent = ComponentType<LucideProps>;

interface SidebarProps {
  sections: SidebarSection[];
  isOpen: boolean;
}

/** Resolve a lucide-react icon by name string */
function getIcon(name: string): IconComponent {
  const iconMap = icons as unknown as Record<string, IconComponent | undefined>;
  return iconMap[name] ?? Circle;
}

interface SidebarNavItemProps {
  label: string;
  icon: string;
  path: string | undefined;
  children: { label: string; path: string }[] | undefined;
}

function SidebarNavItem({ label, icon, path, children }: SidebarNavItemProps) {
  const location = useLocation();
  const Icon = getIcon(icon);

  const isChildActive = children?.some((child) => location.pathname === child.path) ?? false;
  const [isExpanded, setIsExpanded] = useState(isChildActive);

  const toggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Item with children (collapsible section)
  if (children && children.length > 0) {
    return (
      <div>
        <button
          type="button"
          onClick={toggleExpand}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
            'text-slate-300 hover:bg-slate-700/50 hover:text-white',
            isChildActive && 'text-white'
          )}
        >
          <Icon size={18} className="shrink-0" />
          <span className="flex-1 text-left">{label}</span>
          <ChevronDown
            size={16}
            className={cn(
              'shrink-0 transition-transform duration-200',
              isExpanded && 'rotate-180'
            )}
          />
        </button>
        {isExpanded && (
          <div className="ml-4 mt-1 space-y-0.5 border-l border-slate-700 pl-3">
            {children.map((child) => (
              <NavLink
                key={child.path}
                to={child.path}
                className={({ isActive }) =>
                  cn(
                    'block rounded-lg px-3 py-1.5 text-sm transition-colors',
                    isActive
                      ? 'bg-slate-700/70 font-medium text-white'
                      : 'text-slate-400 hover:bg-slate-700/30 hover:text-slate-200'
                  )
                }
              >
                {child.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Simple item with direct path
  if (path) {
    return (
      <NavLink
        to={path}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
            isActive
              ? 'bg-slate-700/70 text-white'
              : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
          )
        }
      >
        <Icon size={18} className="shrink-0" />
        <span>{label}</span>
      </NavLink>
    );
  }

  return null;
}

const ROLE_CONFIG = {
  BACKOFFICE: { label: 'Fiado SA', shortLabel: 'FSA', color: 'bg-blue-600' },
  PARTNER: { label: 'UNE-Credito', shortLabel: 'UC', color: 'bg-emerald-600' },
} as const;

function RoleSwitcher() {
  const setActiveRole = useAppStore((s) => s.setActiveRole);
  const { resolvedRole: currentRole } = useAudience();

  const current = ROLE_CONFIG[currentRole as keyof typeof ROLE_CONFIG] ?? ROLE_CONFIG.PARTNER;
  const otherRole = currentRole === 'BACKOFFICE' ? 'PARTNER' : 'BACKOFFICE';
  const other = ROLE_CONFIG[otherRole];

  const handleSwitch = useCallback(() => {
    setActiveRole(otherRole);
  }, [otherRole, setActiveRole]);

  return (
    <div className="px-3 pb-3">
      <button
        type="button"
        onClick={handleSwitch}
        className="flex w-full items-center gap-3 rounded-lg border border-slate-700/50 bg-slate-800/50 px-3 py-2.5 text-sm transition-colors hover:bg-slate-700/50"
      >
        <div className={cn('flex h-7 w-7 items-center justify-center rounded-md text-xs font-bold text-white', current.color)}>
          {current.shortLabel}
        </div>
        <div className="flex-1 text-left">
          <p className="font-medium text-white">{current.label}</p>
          <p className="text-xs text-slate-400">Cambiar a {other.label}</p>
        </div>
        <ArrowLeftRight size={14} className="text-slate-400" />
      </button>
    </div>
  );
}

export function Sidebar({ sections, isOpen }: SidebarProps) {
  const userPermissions = useAuthStore((s) => s.permissions);
  // Solo Fiado SA (rol une_fiadosa) puede alternar entre vistas; un PARTNER (solo UNE) se queda en la suya.
  const { canSwitchRole } = useAudience();

  const visibleSections = useMemo(
    () => filterSectionsByPermissions(sections, userPermissions),
    [sections, userPermissions]
  );

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-30 flex w-64 flex-col bg-slate-900 transition-transform duration-200 lg:static lg:translate-x-0',
        !isOpen && '-translate-x-full'
      )}
    >
      {/* Logo area */}
      <div className="flex h-16 items-center gap-2.5 border-b border-slate-700/50 px-5">
        <img src={fiadoIsotipo} alt="Fiado" className="h-8" style={{ width: 'auto' }} />
        <span className="text-lg font-semibold text-white">Fiado Credit</span>
      </div>

      {/* Role Switcher (solo admins Fiado SA) */}
      {canSwitchRole && (
        <div className="border-b border-slate-700/50 py-3">
          <RoleSwitcher />
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <div className="space-y-6">
          {visibleSections.map((section) => (
            <div key={section.title}>
              <h3 className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                {section.title}
              </h3>
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <SidebarNavItem
                    key={item.label}
                    label={item.label}
                    icon={item.icon}
                    path={item.path}
                    children={item.children}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </nav>

      {/* Footer */}
      <div className="border-t border-slate-700/50 px-5 py-3">
        <p className="text-xs text-slate-500">Fiado Credit v1.0</p>
      </div>
    </aside>
  );
}
